import React, { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, Globe } from 'lucide-react';

// Curated primary nav for the desktop bar; the full set lives in the mobile
// menu, the Home index grid, and the footer.
const primaryNav = ['cv', 'research', 'publications', 'teaching', 'conferences', 'contact'];
const fullNav = ['cv', 'research', 'publications', 'teaching', 'conferences', 'projects', 'experiments', 'contact'];

const Header = () => {
  const { t, i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const toggleLanguage = () => i18n.changeLanguage(i18n.language === 'en' ? 'de' : 'en');

  const linkClass = ({ isActive }) =>
    `text-sm tracking-wide transition-colors duration-200 ${
      isActive ? 'text-navy-900 font-semibold' : 'text-ink-600 hover:text-navy-700'
    }`;

  return (
    <header className="sticky top-0 z-50 border-b border-line bg-beige-100/85 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 md:px-10">
        <Link
          to="/"
          className="font-display text-xl font-semibold tracking-tight text-navy-900 transition-colors hover:text-navy-700"
        >
          {t('name')}
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-7 md:flex">
          {primaryNav.map((item) => (
            <NavLink key={item} to={`/${item}`} className={linkClass}>
              {t(`nav.${item}`)}
            </NavLink>
          ))}
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-1.5 rounded-sm border border-navy-600/30 px-2.5 py-1 text-xs font-medium uppercase tracking-label text-navy-600 transition-colors hover:border-navy-600 hover:text-navy-900"
            aria-label={i18n.language === 'en' ? 'Auf Deutsch umschalten' : 'Switch to English'}
          >
            <Globe size={12} strokeWidth={1.75} />
            {i18n.language === 'en' ? 'DE' : 'EN'}
          </button>
        </nav>

        {/* Mobile trigger */}
        <button
          className="text-navy-900 md:hidden"
          onClick={() => setIsOpen((v) => !v)}
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isOpen}
        >
          {isOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden border-t border-line bg-beige-100 md:hidden"
          >
            <div className="flex flex-col px-6 py-2">
              {fullNav.map((item) => (
                <NavLink
                  key={item}
                  to={`/${item}`}
                  onClick={() => setIsOpen(false)}
                  className={({ isActive }) =>
                    `border-b border-line/60 py-3 text-sm tracking-wide last:border-0 ${
                      isActive ? 'text-navy-900 font-semibold' : 'text-ink-600'
                    }`
                  }
                >
                  {t(`nav.${item}`)}
                </NavLink>
              ))}
              <button
                onClick={() => {
                  toggleLanguage();
                  setIsOpen(false);
                }}
                className="flex items-center gap-2 py-3 text-sm font-medium uppercase tracking-label text-navy-600"
              >
                <Globe size={13} strokeWidth={1.75} />
                {i18n.language === 'en' ? 'Deutsch' : 'English'}
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
